import React, { useEffect, useMemo, useState } from 'react';
import MessageDisplay from '../components/MessageDisplay';
import LoadingSpinner from '../components/LoadingSpinner';
import FilterBar from '../components/FilterBar/FilterBar';
import DataCard from '../components/DataCard/DataCard';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import { enrollmentsAPI, mockInterviewsAPI } from '../services/api';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import { formatDate } from '../utils/formatUtils';
import StaffPageShell from '../components/StaffPageShell';

const EMPTY_INTERVIEW = {
  id: '',
  interviewDate: '',
  interviewerName: '',
  interviewCategory: '',
  moduleName: '',
  feedback: ''
};

const hasInterviewData = (interview) =>
  Boolean(
    interview.interviewDate ||
      interview.interviewerName ||
      interview.interviewCategory ||
      interview.moduleName ||
      interview.feedback
  );

const MockInterviewsPage = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    interview1: { ...EMPTY_INTERVIEW },
    interview2: { ...EMPTY_INTERVIEW }
  });

  const { loading, error, success, execute, setSuccess } = useAsyncOperation();

  const fetchEnrollments = async () => {
    const response = await execute(async () => enrollmentsAPI.getAll());
    if (response?.data !== undefined) {
      setEnrollments(normalizeApiListResponse(response.data));
    }
  };

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const batches = useMemo(() => {
    const unique = new Map();
    enrollments.forEach((enrollment) => {
      if (enrollment?.batch?.id) {
        unique.set(enrollment.batch.id, enrollment.batch.batchName || `Batch ${enrollment.batch.id}`);
      }
    });
    return Array.from(unique.entries()).map(([id, name]) => ({ id, name }));
  }, [enrollments]);

  const filteredEnrollments = useMemo(() => {
    return enrollments.filter((enrollment) => {
      if (selectedBatch && String(enrollment?.batch?.id) !== String(selectedBatch)) {
        return false;
      }

      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const studentName = enrollment?.student?.fullName?.toLowerCase() || '';
      const studentEmail = enrollment?.student?.emailAddress?.toLowerCase() || '';
      const batchName = enrollment?.batch?.batchName?.toLowerCase() || '';
      const courseName = enrollment?.batch?.course?.courseName?.toLowerCase() || '';

      return (
        studentName.includes(term) ||
        studentEmail.includes(term) ||
        batchName.includes(term) ||
        courseName.includes(term)
      );
    });
  }, [enrollments, selectedBatch, searchTerm]);

  const openModal = (enrollment) => {
    const interviews = Array.isArray(enrollment?.mockInterviews) ? enrollment.mockInterviews : [];
    const interview1 = interviews.find((i) => Number(i.interviewNumber) === 1);
    const interview2 = interviews.find((i) => Number(i.interviewNumber) === 2);

    setSelectedEnrollment(enrollment);
    setFormData({
      interview1: {
        id: interview1?.id || '',
        interviewDate: interview1?.interviewDate ? interview1.interviewDate.split('T')[0] : '',
        interviewerName: interview1?.interviewerName || '',
        interviewCategory: interview1?.interviewCategory || '',
        moduleName: interview1?.moduleName || '',
        feedback: interview1?.feedback || ''
      },
      interview2: {
        id: interview2?.id || '',
        interviewDate: interview2?.interviewDate ? interview2.interviewDate.split('T')[0] : '',
        interviewerName: interview2?.interviewerName || '',
        interviewCategory: interview2?.interviewCategory || '',
        moduleName: interview2?.moduleName || '',
        feedback: interview2?.feedback || ''
      }
    });
    setShowModal(true);
  };

  const updateInterviewField = (key, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleDeleteInterview = async (interviewKey) => {
    const target = formData[interviewKey];
    if (!target?.id) return;

    const response = await execute(
      async () => mockInterviewsAPI.delete(target.id),
      { successMessage: 'Mock interview deleted successfully.' }
    );
    if (!response) return;

    setFormData((prev) => ({
      ...prev,
      [interviewKey]: { ...EMPTY_INTERVIEW }
    }));
    await fetchEnrollments();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEnrollment?.id) return;

    const payloads = [
      { key: 'interview1', interviewNumber: 1, ...formData.interview1 },
      { key: 'interview2', interviewNumber: 2, ...formData.interview2 }
    ];

    const toSave = payloads.filter((item) => hasInterviewData(item));

    const response = await execute(
      async () =>
        Promise.all(
          toSave.map((item) =>
            mockInterviewsAPI.upsert({
              enrollmentId: selectedEnrollment.id,
              interviewNumber: item.interviewNumber,
              interviewDate: item.interviewDate || null,
              interviewerName: item.interviewerName || null,
              interviewCategory: item.interviewCategory || null,
              moduleName: item.moduleName || null,
              feedback: item.feedback || null
            })
          )
        ),
      { successMessage: 'Mock interview details saved successfully.' }
    );

    if (!response) return;

    setShowModal(false);
    setSelectedEnrollment(null);
    await fetchEnrollments();
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <StaffPageShell title="Mock Interviews" wide>
        <MessageDisplay message={error || success} />

        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by student, email, batch, or course..."
          filters={[
            {
              name: 'batch',
              label: 'Filter by Batch',
              value: selectedBatch || '',
              onChange: setSelectedBatch,
              type: 'select',
              options: [{ value: '', label: 'All Batches' }, ...batches.map((b) => ({ value: String(b.id), label: b.name }))]
            }
          ]}
        />

        {loading && !enrollments.length ? (
          <LoadingSpinner message="Loading enrollments..." />
        ) : filteredEnrollments.length === 0 ? (
          <div className="text-center py-12 theme-card rounded-lg">
            <p className="text-lg theme-text-secondary">No enrollments found matching filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredEnrollments.map((enrollment) => {
              const mock1 = enrollment?.mockInterviews?.find((m) => Number(m.interviewNumber) === 1);
              const mock2 = enrollment?.mockInterviews?.find((m) => Number(m.interviewNumber) === 2);

              return (
                <DataCard
                  key={enrollment.id}
                  title={enrollment?.student?.fullName || 'Unknown Student'}
                  badges={[
                    {
                      label: `${enrollment?.mockInterviews?.length || 0} interviews`,
                      className: 'bg-blue-100 text-blue-700'
                    }
                  ]}
                  metadata={[
                    { label: 'Batch', value: enrollment?.batch?.batchName || 'N/A' },
                    { label: 'Course', value: enrollment?.batch?.course?.courseName || 'N/A' },
                    { label: 'Email', value: enrollment?.student?.emailAddress || 'N/A' },
                    { label: 'Enrollment Date', value: formatDate(enrollment?.enrollmentDate) }
                  ]}
                  actions={[
                    {
                      label: 'Edit',
                      onClick: () => openModal(enrollment),
                      variant: 'edit'
                    }
                  ]}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold theme-text mb-2">Interview 1</p>
                      <p className="text-xs theme-text-secondary">Date: {mock1 ? formatDate(mock1.interviewDate) : 'Not set'}</p>
                      <p className="text-xs theme-text-secondary">Interviewer: {mock1?.interviewerName || 'Not set'}</p>
                      <p className="text-xs theme-text-secondary">Category: {mock1?.interviewCategory || 'Not set'}</p>
                    </div>
                    <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold theme-text mb-2">Interview 2</p>
                      <p className="text-xs theme-text-secondary">Date: {mock2 ? formatDate(mock2.interviewDate) : 'Not set'}</p>
                      <p className="text-xs theme-text-secondary">Interviewer: {mock2?.interviewerName || 'Not set'}</p>
                      <p className="text-xs theme-text-secondary">Category: {mock2?.interviewCategory || 'Not set'}</p>
                    </div>
                  </div>
                </DataCard>
              );
            })}
          </div>
        )}

        <Modal
          isOpen={showModal && !!selectedEnrollment}
          onClose={() => {
            setShowModal(false);
            setSelectedEnrollment(null);
          }}
          title={`Mock Interview - ${selectedEnrollment?.student?.fullName || 'Student'}`}
          size="xl"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {[
              { key: 'interview1', number: 1 },
              { key: 'interview2', number: 2 }
            ].map((item) => {
              const interview = formData[item.key];
              const showModuleName = (interview.interviewCategory || '').toLowerCase().includes('module');
              return (
                <div key={item.key} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold theme-text">Interview {item.number}</h3>
                    {interview.id && (
                      <button
                        type="button"
                        onClick={() => handleDeleteInterview(item.key)}
                        className="text-sm px-3 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        Delete Interview {item.number}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-1">Interview Date</label>
                      <input
                        type="date"
                        value={interview.interviewDate}
                        onChange={(e) => updateInterviewField(item.key, 'interviewDate', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-1">Interviewer Name</label>
                      <input
                        type="text"
                        value={interview.interviewerName}
                        onChange={(e) => updateInterviewField(item.key, 'interviewerName', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                        placeholder="Enter interviewer name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium theme-text-secondary mb-1">Interview Category</label>
                      <SearchableSelect
                        value={interview.interviewCategory}
                        onChange={(e) => updateInterviewField(item.key, 'interviewCategory', e.target.value)}
                        className="w-full"
                        placeholder="Select category"
                        options={[
                          { value: 'Module Based', label: 'Module Based' },
                          { value: 'Course based', label: 'Course based' }
                        ]}
                      />
                    </div>
                    {showModuleName && (
                      <div>
                        <label className="block text-sm font-medium theme-text-secondary mb-1">Module Name</label>
                        <input
                          type="text"
                          value={interview.moduleName}
                          onChange={(e) => updateInterviewField(item.key, 'moduleName', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg theme-input"
                          placeholder="Enter module name"
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium theme-text-secondary mb-1">Feedback</label>
                    <textarea
                      value={interview.feedback}
                      onChange={(e) => updateInterviewField(item.key, 'feedback', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg theme-input"
                      rows="3"
                      placeholder={`Interview ${item.number} feedback`}
                    />
                  </div>
                </div>
              );
            })}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setSelectedEnrollment(null);
                }}
                className="px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Mock Interviews'}
              </button>
            </div>
          </form>
        </Modal>
    </StaffPageShell>
  );
};

export default MockInterviewsPage;
